import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import * as showdown from 'showdown';
import { Subscription, firstValueFrom } from 'rxjs';
import { getAuth } from 'firebase/auth';

import { ChatFireService } from 'src/app/gen2ai/chatfire.service';
import { Message } from '../../models/chat.model';
import { TourStep, TourService } from 'src/app/authentication/tour.service';
import { AuthenticationService } from 'src/app/clari-auth/authentication.service';
import { NotesService } from '../../services/notes.service';
import { TheVoiceComponent } from '../dialogs/the-voice/the-voice.component';
import { AiCatalogComponent } from '../dialogs/ai-catalog/ai-catalog.component';
import { MediaLibraryService } from 'src/app/gen2ai/media-library.service';

interface MediaModelOption {
  value: 'veo31' | 'cogx';
  label: string;
  icon: string;
  kind: 'image' | 'video';
}

interface MediaCatalogCategory {
  title: string;
  models: Array<{ name: string; value: string; icon?: string; iconUrl?: string }>;
}

@Component({
  selector: 'app-clari-video-chat',
  templateUrl: './clari-video-chat.component.html',
  styleUrls: ['./clari-video-chat.component.scss']
})
export class ClariVideoChatComponent implements OnInit, AfterViewInit, OnDestroy {
  public userInput = '';
  public currentMessages: Message[] = [];
  public pinnedMessages: Message[] = [];

  public selectedMediaModel: 'veo31' | 'cogx' = 'veo31';
  public dropdownOpen = false;

  public readonly mediaModels: MediaModelOption[] = [
    {
      value: 'veo31',
      label: 'Veo 3.1',
      icon: '🎥',
      kind: 'video'
    },
    {
      value: 'cogx',
      label: 'CogX',
      icon: '🎬',
      kind: 'video'
    }
  ];

  private readonly mediaCatalogCategories: MediaCatalogCategory[] = [
    {
      title: '🎥 Video Generation',
      models: [
        { name: 'Veo 3.1', value: 'veo31', iconUrl: 'assets/logos/google.svg' },
        { name: 'CogX', value: 'cogx', iconUrl: 'assets/logos/zai.svg' }
      ]
    }
  ];

  public referenceImageFile: File | null = null;
  public referenceImagePreviewUrl: string | null = null;
  public imageValidationError: string | null = null;
  public error: string | null = null;

  public converter: showdown.Converter;
  public audioActive = false;
  public showScrollToBottom = false;
  public showPinStack = false;
  public openActionsMessageId: string | null = null;
  public copiedMessageId: number | null = null;
  public actionToastById: Record<string, { text: string; type: 'ok' | 'warn' }> = {};

  public generating = false;
  public uiProgress = 0;
  public isRequestInProgress = false;

  /** Integer percent for the generating bar (avoids `number` pipe when this template is compiled from multiple modules). */
  get uiProgressRounded(): number {
    return Math.round(this.uiProgress);
  }

  private userScrolledUp = false;
  private authSub?: Subscription;
  private subs: Subscription[] = [];
  private currentPinnedIndex = -1;
  private videoUiRunId: string | null = null;
  private videoRunStartMs = 0;
  private activeRequestModel: 'veo31' | 'cogx' | null = null;
  private persistedMediaMessageKeys = new Set<string>();

  private readonly allowedImageTypesByModel: Record<string, string[]> = {
    veo31: ['image/png', 'image/jpeg', 'image/webp']
  };

  @ViewChild('messagesContainer', { static: false })
  private messagesContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('videoTextarea')
  public videoTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('referenceImageInput')
  public referenceImageInput?: ElementRef<HTMLInputElement>;

  constructor(
    private chatService: ChatFireService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private tourService: TourService,
    private authService: AuthenticationService,
    private notesService: NotesService,
    private mediaLibraryService: MediaLibraryService
  ) {
    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true,
      ghCodeBlocks: true,
      smoothLivePreview: true
    });
  }

  public get selectedModel(): MediaModelOption | undefined {
    return this.mediaModels.find(model => model.value === this.selectedMediaModel);
  }

  public get selectedModelName(): string {
    return this.selectedModel?.label || 'Veo 3.1';
  }

  public get placeholderModelName(): string {
    return this.hasPremiumAccess ? this.selectedModelName : 'Coming Soon';
  }

  public get currentLogo(): string {
    return this.hasPremiumAccess ? 'assets/logos/google.svg' : 'assets/illustrations/coming-soon-features.svg';
  }

  public get currentPlaceholder(): string {
    return 'Generate World Class Videos';
  }

  public get composerPlaceholder(): string {
    return 'Describe the video you want to create...';
  }

  public get imageAccept(): string {
    const types = this.allowedImageTypesByModel[this.selectedMediaModel] || [];
    return types.join(',');
  }

  public get referencePreviewUrl(): string | null {
    return this.referenceImagePreviewUrl;
  }

  public get supportsReferenceImage(): boolean {
    return this.modelSupportsReferenceImage(this.selectedMediaModel);
  }

  public get canSubmit(): boolean {
    if (!this.hasPremiumAccess) {
      return false;
    }

    if (this.audioActive || this.isRequestInProgress) {
      return false;
    }

    const prompt = this.userInput.trim();

    return !!prompt || !!this.referenceImageFile;
  }

  public get hasPremiumAccess(): boolean {
    // Placeholder gate until premium auth tags are wired.
    return false;
  }

  /** Video-only composer in ClariVideoChat. */
  public get isVideoComposerModel(): boolean {
    return true;
  }

  public get thinkingLoaderLabel(): string {
    return 'Generating video';
  }

  public get submitIcon(): string {
    return 'fa-play';
  }

  public getSelectedModel(): MediaModelOption | undefined {
    return this.selectedModel;
  }

  public selectMediaModel(value: string): void {
    if (!this.hasPremiumAccess) {
      this.openPremiumPlaceholder();
      return;
    }
    this.selectedMediaModel = this.normalizeMediaModel(value);
    this.dropdownOpen = false;
    this.imageValidationError = null;
    this.error = null;
  }

  public toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }

  public openMediaModelDialog(event?: Event): void {
    event?.stopPropagation();
    this.dropdownOpen = false;
    this.error = null;

    const ref = this.dialog.open(AiCatalogComponent, {
      panelClass: 'ai-catalog-dialog-panel',
      width: '900px',
      maxWidth: '98vw',
      height: 'auto',
      maxHeight: '95vh',
      autoFocus: false,
      data: {
        context: 'clarivideochat',
        title: 'Select Media Model',
        categories: this.mediaCatalogCategories,
        selectedModel: this.selectedMediaModel
      }
    });

    ref.afterClosed().subscribe((result?: { value?: string }) => {
      if (!result?.value) return;
      this.selectMediaModel(result.value);
      this.cdr.markForCheck();
    });
  }

  public openPremiumPlaceholder(event?: Event): void {
    event?.stopPropagation();
    this.error = null;
  }

  private normalizeMediaModel(model: string): 'veo31' | 'cogx' {
    const id = String(model || '').toLowerCase().trim();

    if (['veo', 'veo3', 'veo31', 'veo 3.1', 'video'].includes(id)) {
      return 'veo31';
    }

    if (['cogx', 'cog', 'cogvideo', 'cogvideox3'].includes(id)) {
      return 'cogx';
    }

    return 'veo31';
  }

  public autoResize(el: HTMLTextAreaElement): void {
    const maxHeight = 240;

    if (!el.value || el.value.trim().length === 0) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      el.scrollTop = 0;
      return;
    }

    const computedStyle = window.getComputedStyle(el);
    const lineHeight = parseFloat(computedStyle.lineHeight || '20') || 20;

    el.style.height = '';

    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight || lineHeight;
    const fillRatio = scrollHeight / clientHeight;
    const shouldPreGrow = fillRatio >= 0.85;
    const desiredHeight = Math.min(scrollHeight + (shouldPreGrow ? lineHeight : 0), maxHeight);

    el.style.height = `${desiredHeight}px`;

    const capped = desiredHeight >= maxHeight;
    el.style.overflowY = capped ? 'auto' : 'hidden';

    if (capped) {
      el.scrollTop = el.scrollHeight;
    }
  } 

  private resetTextarea(): void {
    const el = this.videoTextarea?.nativeElement;
    if (!el) {
      return;
    }

    el.style.height = '';
    el.style.overflowY = 'hidden';
    el.scrollTop = 0;
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const insidePins = !!target.closest('.pin-stack-anchor');
    if (!insidePins && this.showPinStack) {
      this.ngZone.run(() => (this.showPinStack = false));
    }

    const clickedInsideActions = !!target.closest('.assistant-actions');
    if (!clickedInsideActions) {
      this.openActionsMessageId = null;
    }

    const insideDropdown = !!target.closest('.video-dropdown-root');
    if (!insideDropdown && this.dropdownOpen) {
      this.ngZone.run(() => (this.dropdownOpen = false));
    }
  }

  public async ngAfterViewInit(): Promise<void> {
    if (this.messagesContainer?.nativeElement) {
      this.messagesContainer.nativeElement.addEventListener('scroll', () => {
        const container = this.messagesContainer.nativeElement;
        const threshold = 50;
        const atBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;

        this.userScrolledUp = !atBottom;
        this.showScrollToBottom = !atBottom;
      });
    }
  }

  public ngOnInit(): void {
    this.chatService.setChatHistoryNamespace('video').subscribe();

    this.subs.push(
      this.route.queryParamMap.subscribe((params) => {
        const selectedModel = params.get('selectedModel');
        if (!selectedModel) return;
        this.selectMediaModel(selectedModel);
      })
    );

    this.subs.push(
      this.chatService.currentConversation$.subscribe(conversation => {
        if (!conversation?.messages?.length) {
          return;
        }

        this.mergeIncomingMessages(conversation.messages);
        this.scrollToBottomIfNeeded();
        this.refreshPinnedMessages();
        this.checkVideoCompletionFromMessages();
      })
    );

    this.authSub = this.authService.currentUser$.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.authSub?.unsubscribe();
    this.stopVideoUiProgress(false);
  }

  private mergeIncomingMessages(incoming: Message[]): void {
    const messageMap = new Map<string, Message>();

    [...this.currentMessages, ...(incoming || [])].forEach(message => {
      const key = this.getMessageKey(message);
      const existing = messageMap.get(key);
      messageMap.set(key, existing ? ({ ...existing, ...message } as Message) : message);
    });

    const mergedSorted = Array.from(messageMap.values()).sort((a: any, b: any) => {
      const aTime = a?.timestamp ? new Date(a.timestamp as any).getTime() : 0;
      const bTime = b?.timestamp ? new Date(b.timestamp as any).getTime() : 0;
      return aTime - bTime;
    });

    this.currentMessages = this.extractMediaTimeline(mergedSorted);

    this.applyVideoJobProgressToUiBar();

    void this.persistGeneratedMediaToLibrary();
  }

  private extractMediaTimeline(messages: Message[]): Message[] {
    const mergedSorted = [...messages].sort((a: any, b: any) => {
      const aTime = a?.timestamp ? new Date(a.timestamp as any).getTime() : 0;
      const bTime = b?.timestamp ? new Date(b.timestamp as any).getTime() : 0;
      return aTime - bTime;
    });

    const out: Message[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < mergedSorted.length; i++) {
      const msg: any = mergedSorted[i];
      if (!this.isMediaMessage(msg)) {
        continue;
      }

      const prev: any = i > 0 ? mergedSorted[i - 1] : null;
      if (prev?.role === 'user') {
        const prevKey = this.getMessageKey(prev);
        if (prevKey && !seen.has(prevKey)) {
          out.push(prev);
          seen.add(prevKey);
        }
      }

      const key = this.getMessageKey(msg);
      if (key && !seen.has(key)) {
        out.push(msg);
        seen.add(key);
      }
    }

    // User prompt waiting on image/video (e.g. Stability) — no assistant media row yet, so show the request + loader.
    const last = mergedSorted[mergedSorted.length - 1] as any;
    if (last?.role === 'user') {
      const key = this.getMessageKey(last);
      if (key && !seen.has(key)) {
        out.push(last);
        seen.add(key);
      }
    }

    return out.sort((a: any, b: any) => {
      const aTime = a?.timestamp ? new Date(a.timestamp as any).getTime() : 0;
      const bTime = b?.timestamp ? new Date(b.timestamp as any).getTime() : 0;
      return aTime - bTime;
    });
  }

  private isMediaMessage(message: Message | any): boolean {
    return this.isImageMessage(message) || this.isVideoMessage(message);
  }

  private async persistGeneratedMediaToLibrary(): Promise<void> {
    const auth = getAuth();
    if (!auth.currentUser?.uid) {
      return;
    }

    for (const message of this.currentMessages) {
      if (message.role !== 'assistant') continue;
      const key = this.getMessageKey(message);
      if (!key || this.persistedMediaMessageKeys.has(key)) continue;

      const imageUrl = String((message as any)?.image || '').trim();
      const videoUrl = String((message as any)?.video || '').trim();

      if (!imageUrl && !videoUrl) continue;

      try {
        if (imageUrl) {
          const imageFile = await this.mediaUrlToFile(imageUrl, `clarivideo-${Date.now()}.png`, 'image/png');
          await firstValueFrom(
            this.mediaLibraryService.uploadMediaToGcsWithProgress(imageFile, {
              title: message.modelDisplayName || 'Generated Image'
            })
          );
        } else if (videoUrl) {
          const videoFile = await this.mediaUrlToFile(videoUrl, `clarivideo-${Date.now()}.mp4`, 'video/mp4');
          await firstValueFrom(
            this.mediaLibraryService.uploadMediaToGcsWithProgress(videoFile, {
              title: message.modelDisplayName || 'Generated Video'
            })
          );
        }
        this.persistedMediaMessageKeys.add(key);
        await this.mediaLibraryService.loadAllMediaForUser();
      } catch {
        // Non-blocking: generation UX should not fail if media library upload fails.
      }
    }
  }

  private async mediaUrlToFile(
    source: string,
    fallbackName: string,
    fallbackType: string
  ): Promise<File> {
    const value = String(source || '').trim();
    const fetched = await fetch(value);
    if (!fetched.ok) {
      throw new Error('Unable to fetch generated media for library upload.');
    }
    const blob = await fetched.blob();
    const guessedType = blob.type || fallbackType;
    const ext = guessedType.includes('video') ? 'mp4' : guessedType.includes('jpeg') ? 'jpg' : 'png';
    const name = fallbackName.includes('.') ? fallbackName : `${fallbackName}.${ext}`;
    return new File([blob], name, { type: guessedType });
  }

  private getMessageKey(message: Message | any): string {
    return (
      message?.id ||
      [message?.role || '', message?.timestamp || '', message?.content || '', message?.modelId || ''].join('|')
    );
  }

  private refreshPinnedMessages(): void {
    this.pinnedMessages = this.currentMessages.filter(message => !!message.pinned);
  }

  public onAudioIconClick(): void {
    const dialogRef = this.dialog.open(TheVoiceComponent, {
      width: '98vw',
      height: '98vh',
      maxWidth: '98vw',
      maxHeight: '98vh',
      panelClass: 'voice-dialog-panel',
      autoFocus: false,
      restoreFocus: true,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(() => {
      this.audioActive = false;
    });

    this.audioActive = true;
  }

  public onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }

    void this.handleReferenceImageSelection(file);
  }

  public onPickImage(event: Event): void {
    this.onFileSelected(event);
  }

  public clearImage(): void {
    this.referenceImageFile = null;
    this.referenceImagePreviewUrl = null;
    this.imageValidationError = null;
    if (this.referenceImageInput?.nativeElement) {
      this.referenceImageInput.nativeElement.value = '';
    }
  }

  private modelSupportsReferenceImage(model: string): boolean {
    return Array.isArray(this.allowedImageTypesByModel[model]) && this.allowedImageTypesByModel[model].length > 0;
  }

  private isAllowedReferenceType(file: File, model: string): boolean {
    const allowed = this.allowedImageTypesByModel[model] || [];
    return allowed.includes(file.type);
  }

  private async handleReferenceImageSelection(file: File): Promise<void> {
    this.imageValidationError = null;
    this.error = null;

    if (!this.modelSupportsReferenceImage(this.selectedMediaModel)) {
      this.clearImage();
      this.imageValidationError = `${this.selectedModelName} does not accept reference images.`;
      return;
    }

    if (!this.isAllowedReferenceType(file, this.selectedMediaModel)) {
      const allowed = this.allowedImageTypesByModel[this.selectedMediaModel] || [];
      this.clearImage();
      this.imageValidationError = `${this.selectedModelName} only accepts ${allowed
        .map(type => type.split('/')[1].toUpperCase())
        .join(', ')} images.`;
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      this.referenceImageFile = file;
      this.referenceImagePreviewUrl = event.target?.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  public async sendMessage(): Promise<void> {
    if (this.isRequestInProgress) {
      return;
    }

    const prompt = this.userInput.trim();

    if (!prompt && !this.referenceImageFile) {
      return;
    }

    this.error = null;
    this.imageValidationError = null;
    this.isRequestInProgress = true;
    this.activeRequestModel = this.selectedMediaModel;
    this.cdr.detectChanges();

    const referenceFile = this.referenceImageFile;

    this.userInput = '';
    this.resetTextarea();

    this.startVeoGeneration(prompt, referenceFile);
    setTimeout(() => this.scrollToBottom(), 50);
  }

  private async generateStability35Large(prompt: string, referenceFile: File | null): Promise<void> {
    try {
      await firstValueFrom(
        this.chatService.sendMessage(prompt, 'stability35l', false, referenceFile || undefined, 1)
      );
      this.clearImage();
    } catch (err: any) {
      this.error = err?.message || 'Failed to generate Stability image.';
    } finally {
      this.isRequestInProgress = false;
      this.activeRequestModel = null;
      this.cdr.detectChanges();
    }
  }

  private startVeoGeneration(prompt: string, referenceFile: File | null): void {
    this.videoUiRunId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    this.videoRunStartMs = Date.now();
    this.startVideoUiProgress();

    if (!referenceFile) {
      this.chatService.sendUiVideo(prompt, 'veo31', { clientRunId: this.videoUiRunId || undefined });
      return;
    }

    this.fileToDataUrl(referenceFile)
      .then((imageBase64) => {
        this.chatService.sendUiVideo(prompt, 'veo31', {
          clientRunId: this.videoUiRunId || undefined,
          imageBase64
        });
      })
      .catch(() => {
        this.error = 'Failed to read reference image for Veo request.';
        this.stopVideoUiProgress(false, 0);
        this.isRequestInProgress = false;
        this.activeRequestModel = null;
        this.videoUiRunId = null;
        this.videoRunStartMs = 0;
        this.cdr.detectChanges();
      });
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Unable to convert file to data URL'));
      reader.readAsDataURL(file);
    });
  }

  private async generateGeminiImage(prompt: string, referenceFile: File | null): Promise<void> {
    try {
      await firstValueFrom(
        this.chatService.sendMessage(
          prompt,
          'geminiproimage',
          false,
          referenceFile || undefined
        )
      );
      this.clearImage();
    } catch (err: any) {
      this.error = err?.message || 'Failed to generate image.';
    } finally {
      this.isRequestInProgress = false;
      this.activeRequestModel = null;
      this.cdr.detectChanges();
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.sendMessage();
    }
  }

  public startTour(): void {
    const steps: TourStep[] = [
      {
        elementId: 'brandingClariVideoChat',
        title: 'Welcome to Clari Studio',
        description: 'Use Veo 3.1 for cinematic video generation.'
      },
      {
        elementId: 'mediaModelDropdown',
        title: 'Model Selection',
        description: 'Veo 3.1 is the video model available in ClariVideoChat.'
      },
      {
        elementId: 'videoPromptInput',
        title: 'Prompt',
        description: 'Describe exactly what you want to create.'
      },
      {
        elementId: 'imageReferenceUpload',
        title: 'Reference Image',
        description: 'Attach an image if you want the generation to follow an existing visual reference.'
      },
      {
        elementId: 'generateMediaButton',
        title: 'Generate',
        description: 'Start the generation with the selected model.'
      }
    ];

    this.tourService.startTour(steps);
  }

  public convertMarkdown(markdown: string): string {
    try {
      return this.converter.makeHtml(markdown || '');
    } catch {
      return markdown || '';
    }
  }

  public isImageMessage(message: Message | any): boolean {
    const modelId = String(message?.modelId || '').toLowerCase();
    return (
      Boolean(message?.image || (Array.isArray(message?.images) && message.images.length > 0)) ||
      ['geminiproimage', 'stability35l', 'geminiimage', 'googleimage', 'image'].includes(modelId)
    );
  }

  public isVideoMessage(message: Message | any): boolean {
    const modelId = String(message?.modelId || '').toLowerCase();
    return (
      Boolean(message?.videoId || message?.video || message?.thumbnail) ||
      ['veo', 'veo31', 'cogvideox3', 'cogx', 'novareel11', 'nova-reel-1.1', 'video'].includes(modelId)
    );
  }

  private startVideoUiProgress(): void {
    this.stopVideoUiProgress(false, 0);
    this.generating = true;
    this.uiProgress = 0;
    this.applyVideoJobProgressToUiBar();
  }

  /**
   * Bottom gen bar follows the active video card’s `progress` from ChatFire / Veo polling (not a fake timer).
   */
  private applyVideoJobProgressToUiBar(): void {
    if (!this.videoUiRunId || !this.isRequestInProgress) {
      return;
    }

    const runId = this.videoUiRunId;
    const videoMsgs = this.currentMessages.filter(m => this.isVideoMessage(m));
    const match = videoMsgs.filter((m: any) => m.clientRunId === runId);
    const pool = match.length ? match : videoMsgs;
    const latest = pool[pool.length - 1] as any;
    if (!latest || typeof latest.progress !== 'number') {
      return;
    }

    this.uiProgress = Math.min(100, Math.max(0, latest.progress));
    this.cdr.detectChanges();
  }

  private stopVideoUiProgress(setToHundred: boolean, finalPct?: number): void {
    this.generating = false;
    if (setToHundred) {
      this.uiProgress = typeof finalPct === 'number' ? finalPct : 100;
    }
    this.cdr.detectChanges();
  }

  private checkVideoCompletionFromMessages(): void {
    if (!this.isRequestInProgress || !this.videoUiRunId || !this.videoRunStartMs) {
      return;
    }

    const runId = this.videoUiRunId;
    const videoMessages = (this.currentMessages || []).filter(message => this.isVideoMessage(message));
    const scoped = videoMessages.filter((m: any) => m.clientRunId === runId);
    const pool = scoped.length ? scoped : videoMessages;
    if (!pool.length) {
      return;
    }

    const latest = pool
      .slice()
      .sort((a: any, b: any) => {
        const aTime = a?.timestamp ? new Date(a.timestamp as any).getTime() : 0;
        const bTime = b?.timestamp ? new Date(b.timestamp as any).getTime() : 0;
        return aTime - bTime;
      })
      .pop() as any;

    if (!latest) {
      return;
    }

    const latestTimestamp = latest.timestamp ? new Date(latest.timestamp as any).getTime() : 0;
    if (latestTimestamp && latestTimestamp + 2000 < this.videoRunStartMs) {
      return;
    }

    const status = String(latest.status || '').toLowerCase();
    const hasVideo = !!latest.video;
    const isTerminal = ['completed', 'complete', 'done', 'failed', 'error', 'cancelled'].includes(status);

    this.applyVideoJobProgressToUiBar();

    if (hasVideo || isTerminal) {
      const failed = ['failed', 'error', 'cancelled'].includes(status) && !hasVideo;
      this.stopVideoUiProgress(true, failed ? Math.min(100, latest.progress ?? 0) : 100);
      this.isRequestInProgress = false;
      this.activeRequestModel = null;
      this.videoUiRunId = null;
      this.videoRunStartMs = 0;
      this.clearImage();
    }
  }

  public scrollToBottom(): void {
    if (!this.messagesContainer?.nativeElement) {
      return;
    }

    const container = this.messagesContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
    this.userScrolledUp = false;
    this.showScrollToBottom = false;
  }

  private scrollToBottomIfNeeded(): void {
    if (!this.messagesContainer?.nativeElement || this.userScrolledUp) {
      return;
    }

    setTimeout(() => {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }, 100);
  }

  public togglePinStack(event?: MouseEvent): void {
    event?.stopPropagation();
    this.showPinStack = !this.showPinStack;
  }

  public jumpToPinned(id?: string): void {
    if (!id) {
      return;
    }

    this.showPinStack = false;
    this.scrollToPinnedForId(id);
  }

  public onPinsHover(open: boolean): void {
    if (this.canHover()) {
      this.showPinStack = open;
    }
  }

  private canHover(): boolean {
    return window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches ?? false;
  }

  public trackMsg = (_: number, message: Message) => message.id ?? (message.timestamp as any);

  public toggleActionsMenu(message: Message, event?: MouseEvent): void {
    event?.stopPropagation();
    const id = message.id || null;
    if (!id) {
      return;
    }

    this.openActionsMessageId = this.openActionsMessageId === id ? null : id;
  }

  public isActionsMenuOpen(message: Message): boolean {
    return !!message.id && this.openActionsMessageId === message.id;
  }

  public onActionPin(message: Message): void {
    this.togglePinMessage(message);
    this.openActionsMessageId = null;
  }

  public onActionCopy(message: any, index: number): void {
    this.copyMessageContent(message, index);
    this.showActionToast(message, 'Copied ✓');
    this.openActionsMessageId = null;
  }

  public async onActionCopyToClariChat(message: Message): Promise<void> {
    await this.saveMessageToNotes(message);
    this.showActionToast(message, 'Added to ClariNotes ✓');
    this.openActionsMessageId = null;
  }

  public async copyMessageContent(message: any, index: number): Promise<void> {
    try {
      await navigator.clipboard.writeText(message.content || '');
      this.copiedMessageId = index;
      setTimeout(() => (this.copiedMessageId = null), 2000);
    } catch (err) {
      console.error('Failed to copy message content:', err);
    }
  }

  public togglePinMessage(message: Message): void {
    message.pinned = !message.pinned;
    this.refreshPinnedMessages();

    if (message.pinned) {
      this.scrollToPinnedForId(message.id || '');
    }
  }

  private scrollToPinnedForId(id: string): void {
    const container = this.messagesContainer?.nativeElement as HTMLElement;
    const element = document.getElementById(`msg-${id}`);

    if (!element || !container) {
      return;
    }

    const top = element.offsetTop - container.clientHeight / 2 + element.clientHeight / 2;
    container.scrollTo({ top, behavior: 'smooth' });

    element.classList.add('pinned-highlight');
    setTimeout(() => element.classList.remove('pinned-highlight'), 1000);
  }

  public scrollToPinned(next: boolean): void {
    if (this.pinnedMessages.length === 0) {
      return;
    }

    if (next) {
      this.currentPinnedIndex = (this.currentPinnedIndex + 1) % this.pinnedMessages.length;
    } else {
      this.currentPinnedIndex =
        (this.currentPinnedIndex - 1 + this.pinnedMessages.length) % this.pinnedMessages.length;
    }

    const targetMessage = this.pinnedMessages[this.currentPinnedIndex];
    const element = document.getElementById(`msg-${targetMessage.id}`);

    if (element) {
      const container = this.messagesContainer?.nativeElement;
      const offsetTop = (element as any).offsetTop - (container as any).offsetTop - 20;
      container.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  }

  public async saveMessageToNotes(message: Message): Promise<void> {
    if (!message?.content?.trim()) {
      return;
    }

    const who = message.role === 'user' ? 'You' : message.modelDisplayName || 'Assistant';
    const timestamp = message.timestamp ? new Date(message.timestamp as any).toLocaleString() : '';
    const titleBase = `Clari Studio – ${who}`;
    const title = timestamp ? `${titleBase} (${timestamp})` : titleBase;

    await this.notesService.createNote({
      title,
      content: message.content,
      labels: ['ClariStudio'],
      color: '#e1f5fe',
      pinned: false
    });
  }

  public clearAllPins(event?: MouseEvent): void {
    event?.stopPropagation();
    this.currentMessages.forEach(message => (message.pinned = false));
    this.refreshPinnedMessages();
    this.showPinStack = false;
  }

  private showActionToast(message: Message, text: string, type: 'ok' | 'warn' = 'ok'): void {
    const id = message.id as any;
    if (!id) {
      return;
    }

    this.actionToastById[id] = { text, type };

    setTimeout(() => {
      if (this.actionToastById[id]?.text === text) {
        delete this.actionToastById[id];
      }
    }, 1300);
  }
}
